import {
  applyOperation,
  buildOrgModel,
  type Change,
  type ChangeKind,
  changePersonId,
  type ChangeSummary,
  diffScenario,
  effectiveSquads,
  emptySquadDirectory,
  type GroupBy,
  movedPersonIds,
  type OrgModel,
  type OrgSnapshot,
  type OrgStats,
  type Scenario,
  type ScenarioOp,
  type Squad,
  type SquadDirectory,
  type SquadRole,
  summarizeChanges,
} from '@tive/core';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  ApiError,
  api,
  type HistoryPayload,
  type OrgMeta,
  type SharedSummary,
  type Viewer,
} from '../lib/api';
import { colorOverrides, type ColorBy } from '../lib/colors';
import { visibleAfterFilters, type FilterResult } from '../lib/filters';
import type { TableView } from '../lib/table';
import type { Theme } from '../lib/theme';
import { capturePng, downloadDataUrl } from '../lib/export-image';
import { defaultCollapsed, layoutOrg } from '../lib/layout';
import { foldableIds, layoutGroups } from '../lib/group-layout';
import type { BoardDrag } from '../lib/squads';
import { buildGroupTree, groupingKey, resolveFolded } from '../lib/group-tree';
import { currentSession } from '../lib/auth';
import { pickScenarioFile } from '../lib/picker';
import { clearDriveState, driveOpenFileId, pinScenario, pinnedScenarioId } from '../lib/tab';
import { downloadJson, slugify } from '../lib/utils';

export type PanelTab = 'changes' | 'history' | 'details';
/** The reporting line, or the operational teams that cut across it. */
/**
 * Which of the three ways of looking at the org is on screen.
 *
 * `people` is the same people as `reporting`, as rows: the chart answers who
 * reports to whom, the board answers who works on what, and neither answers
 * “show me all of it at once”.
 */
export type ChartView = 'reporting' | 'teams' | 'people';

/**
 * Where a team edit lands: on the shared directory, which is how the teams
 * actually are, or on the open scenario, which is how a plan would change them.
 *
 * The board follows it too — editing the base while looking at a plan would let
 * you take somebody off a team the base never had them on.
 */
export type TeamEditTarget = 'base' | 'scenario';

export interface Toast {
  id: string;
  kind: 'info' | 'success' | 'error' | 'remote';
  message: string;
  /**
   * Somewhere to go, rendered as a plain anchor.
   *
   * A link rather than opening it: the URL only exists once the round trip has
   * come back, and a `window.open` after an await is exactly what a popup
   * blocker is for. It is also the wrong moment to steal focus — somebody who
   * just exported a table is usually mid-thought.
   */
  href?: string;
}

export interface DropState {
  targetId: string | null;
  ok: boolean;
  reason: string | null;
}

interface Derived {
  /** Who survives the filters, and how many did not. Derived; never set directly. */
  filtered: FilterResult;
  model: OrgModel | null;
  changes: Change[];
  summary: ChangeSummary;
  movedIds: Set<string>;
  /** The base directory with the scenario's team changes applied. */
  planSquads: SquadDirectory;
  /** Team name -> the colour it has been pinned to, if any. */
  colorOverrides: Map<string, string>;
}

const EMPTY_SUMMARY: ChangeSummary = {
  total: 0,
  moves: 0,
  retitles: 0,
  departmentChanges: 0,
  locationChanges: 0,
  dottedChanges: 0,
  hires: 0,
  departures: 0,
  teamChanges: 0,
  headcountDelta: 0,
};

export interface AppState extends Derived {
  // --- server state ---------------------------------------------------------
  snapshot: OrgSnapshot | null;
  meta: OrgMeta | null;
  stats: OrgStats | null;
  snowflakeConfigured: boolean;
  scenario: Scenario | null;
  scenarioList: SharedSummary[];
  squads: SquadDirectory;
  /**
   * True when the base teams come from the published file. They are one
   * person's curation for the whole company, edited locally and published, so
   * the server refuses edits and the UI stops offering them.
   */
  sharedTeams: boolean;
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  refreshing: boolean;
  live: boolean;
  /**
   * Who else has this plan open, in the order they arrived.
   *
   * Not persisted and not fetched: it arrives over the socket and empties when
   * the socket goes, because presence is a claim about right now and a stale
   * one is worse than none.
   */
  viewers: Viewer[];
  /**
   * What this person may do with the open plan.
   *
   * Drive's answer, carried through the listing. A plan shared for viewing
   * opens like any other and simply cannot be changed — the UI needs to know
   * that before the first edit rather than after it is refused.
   */
  role: 'owner' | 'writer' | 'reader';
  /**
   * Browser key for Google's file picker, or null where there is none.
   *
   * The picker is the only way to reach a plan somebody shared: `drive.file`
   * cannot see a file until it has been picked, so a shared scenario is absent
   * from the list and a file id typed in by hand comes back 404. No key means
   * no picker, which is every local run.
   */
  pickerApiKey: string | null;
  pickerAppId: string | null;
  /**
   * The release this tab loaded against, and whether the server has moved on.
   *
   * A deploy replaces the running revision, so the page in front of somebody is
   * the previous version until it is reloaded. Nothing reloads it for them —
   * that would throw away a plan they are in the middle of — so it says so and
   * lets them pick the moment.
   */
  buildId: string | null;
  updateReady: boolean;
  /** Raised when a reader tries to change a plan; answered by `copyForEditing`. */
  copyPrompt: boolean;
  /** True only while a PNG is being taken; see `exportPng`. */
  exporting: boolean;

  // --- persisted view state -------------------------------------------------
  theme: Theme;
  panelOpen: boolean;
  panelTab: PanelTab;
  collapsedIds: string[];
  /**
   * Which cards on the team board have their children folded away.
   *
   * Shut ones, the same sense as `collapsedIds` and the same reason: one rule
   * for both canvases. It used to list the *open* teams so that a board of a
   * hundred did not spring open as the whole nest — that rule still holds, but
   * it now lives in `defaultFoldedGroups`, which is only consulted when this
   * says nothing about the grouping on screen. A card's id is its path through
   * the levels, so these are keyed by the grouping they belong to — which also
   * lets a grouping with no entry fall through to the default while one with an
   * empty entry means somebody has opened everything.
   */
  foldedGroups: Record<string, string[]>;
  /**
   * Change categories folded away in the panel.
   *
   * Shut ones rather than open ones: a change list is worth reading by default,
   * and a category nobody has folded — a kind added after this was written
   * included — should show.
   */
  foldedChangeKinds: ChangeKind[];
  /**
   * How somebody has arranged each table, keyed by table id.
   *
   * Per browser rather than per scenario: which columns you want to see is a way
   * of working, not part of any plan, and having to set it up again after
   * switching scenarios is the kind of thing that stops people using a view.
   */
  tableViews: Record<string, TableView>;
  /**
   * Bumped when a view should re-frame itself, and read by both canvases.
   *
   * Collapsing everything is the one act where the viewport is certainly wrong
   * afterwards: what was filling the screen has just gone, leaving a handful of
   * cards adrift in the corner of a canvas sized for the whole company. The
   * button is in the toolbar and `fitView` belongs to whichever canvas is
   * mounted, so this is how one reaches the other.
   *
   * Not persisted: it is an instruction, not a setting.
   */
  fitSignal: number;
  /** Whether a table can be sent to Sheets; false with no Google account. */
  sheetsAvailable: boolean;
  /** Whether the change table is over the app. Not persisted: it is a moment. */
  changesTableOpen: boolean;
  focusId: string | null;
  showMinimap: boolean;
  view: ChartView;
  /** Outermost first. What the team view nests by; 1–3 levels. */
  groupLevels: GroupBy[];
  teamEditTarget: TeamEditTarget;
  /** How each scenario was last left. See `ScenarioView`. */
  viewByScenario: Record<string, ScenarioView>;
  /** Which property the cards are coloured by. */
  colorBy: ColorBy;

  // --- ephemeral view state -------------------------------------------------
  query: string;
  departmentFilter: string[];
  /** Squad ids. Highlights, like the department filter — it never hides anyone. */
  squadFilter: string[];
  onlyChanged: boolean;
  selectedId: string | null;
  hireUnderId: string | null;
  paletteOpen: boolean;
  draggingId: string | null;
  drop: DropState;
  pulseId: string | null;
  toasts: Toast[];
  /**
   * The scenario's log, as the history panel reads it. Null until asked for —
   * it is a panel nobody has open most of the time, and the log grows without
   * bound while the change list does not.
   */
  history: HistoryPayload | null;

  // --- actions -------------------------------------------------------------
  bootstrap: () => Promise<void>;
  refreshOrg: () => Promise<void>;
  applyOps: (ops: ScenarioOp[]) => Promise<boolean>;
  loadHistory: () => Promise<void>;
  undo: () => Promise<void>;
  redo: () => Promise<void>;

  selectScenario: (id: string | null, opts?: { announceAs?: Toast['kind'] }) => Promise<void>;
  newScenario: (name: string, description?: string) => Promise<void>;
  renameScenario: (name: string, description?: string) => Promise<void>;
  duplicateScenario: () => Promise<void>;
  deleteScenario: (id: string) => Promise<void>;
  exportScenario: () => Promise<void>;
  exportPng: () => Promise<void>;
  importScenario: (file: unknown) => Promise<void>;
  /** Open a plan from Drive through Google's picker; see `pickerApiKey`. */
  openFromDrive: () => Promise<void>;
  reloadScenarioList: () => Promise<void>;

  loadSquads: () => Promise<void>;
  createSquad: (name: string, description?: string, parentId?: string | null) => Promise<void>;
  renameSquad: (id: string, name: string) => Promise<void>;
  setSquadColor: (id: string, color: string | null) => Promise<void>;
  setSquadParent: (id: string, parentId: string | null) => Promise<void>;
  deleteSquad: (id: string) => Promise<void>;
  setSquadMember: (id: string, personId: string, role: SquadRole) => Promise<void>;
  removeSquadMember: (id: string, personId: string) => Promise<void>;
  /** Take somebody off one team and put them on another, in that order. */
  moveSquadMember: (fromId: string, toId: string, personId: string, role: SquadRole) => Promise<void>;
  /** What is being dragged around the team board, if anything. */
  boardDrag: BoardDrag | null;
  setBoardDrag: (drag: BoardDrag | null) => void;

  adoptRemoteScenario: (scenario: Scenario, message?: string) => void;
  setLive: (live: boolean) => void;
  setViewers: (viewers: Viewer[]) => void;
  /** Called with whatever build the server just reported. */
  noteBuild: (buildId: string | null | undefined) => void;
  dismissCopyPrompt: () => void;
  /** Copy a plan you can only view, so you have one of your own to change. */
  copyForEditing: () => Promise<void>;

  setView: (view: ChartView) => void;
  setGroupLevels: (levels: GroupBy[]) => void;
  setTeamEditTarget: (target: TeamEditTarget) => void;
  setColorBy: (colorBy: ColorBy) => void;

  toggleCollapse: (id: string) => void;
  toggleGroupFold: (id: string) => void;
  toggleChangeGroup: (kind: ChangeKind) => void;
  setTableView: (table: string, view: TableView) => void;
  exportSheet: (title: string, grid: string[][]) => Promise<void>;
  setChangesTableOpen: (open: boolean) => void;
  setCollapsed: (ids: Iterable<string>) => void;
  ensureVisible: (ids: Iterable<string>) => void;
  expandAll: () => void;
  collapseToDepth: (depth: number) => void;
  expandAllGroups: () => void;
  collapseAllGroups: () => void;
  fitLater: () => void;
  revealPerson: (id: string) => void;

  setFocus: (id: string | null) => void;
  setTheme: (theme: Theme) => void;
  togglePanel: () => void;
  openPanel: (tab: PanelTab) => void;
  setPanelTab: (tab: PanelTab) => void;
  toggleMinimap: () => void;
  setQuery: (query: string) => void;
  setDepartmentFilter: (departments: string[]) => void;
  setSquadFilter: (squadIds: string[]) => void;
  setOnlyChanged: (value: boolean) => void;
  select: (id: string | null) => void;
  setHireUnder: (id: string | null) => void;
  setPalette: (open: boolean) => void;
  startDrag: (id: string | null) => void;
  setDrop: (drop: DropState) => void;
  pulse: (id: string | null) => void;

  toast: (kind: Toast['kind'], message: string, href?: string) => void;
  dismissToast: (id: string) => void;
}

/**
 * Everything the UI shows is derived from three things: the snapshot, the
 * scenario, and the shared squad directory. The directory is in here because a
 * scenario's team changes are diffed against it — change a base team and the
 * change list has to move with it.
 */
/**
 * What the person may do with the open plan, out of the list the server just
 * sent. Owner by default: a local run has one person and no sharing at all.
 */
function roleIn(list: SharedSummary[], scenarioId: string | undefined): 'owner' | 'writer' | 'reader' {
  if (!scenarioId) return 'owner';
  return list.find((item) => item.id === scenarioId)?.role ?? 'owner';
}

/**
 * Whether a scenario the server just sent is worth taking.
 *
 * Two saves in flight at once come back in whatever order the network feels
 * like, and the store used to take the last one to arrive. When that was the
 * *earlier* answer the newer change vanished from the chart — a planned hire
 * that was safely on the server and simply not on screen until a reload.
 *
 * `revision` is the log length, so it only ever goes up. Equal is accepted:
 * that is the same state described twice, and refusing it would drop the reply
 * to a change that happened to alter nothing.
 */
function isCurrent(held: Scenario | null, incoming: Scenario): boolean {
  if (!held || held.id !== incoming.id) return true;
  return incoming.revision >= held.revision;
}

function derive(
  snapshot: OrgSnapshot | null,
  scenario: Scenario | null,
  squads: SquadDirectory,
  filters: Pick<AppState, 'onlyChanged' | 'departmentFilter' | 'squadFilter'>,
): Derived {
  const planSquads = effectiveSquads(squads, scenario);
  const overrides = colorOverrides(planSquads);
  const empty = { keep: null, matched: 0, context: 0, total: 0 };
  if (!snapshot) {
    return {
      model: null,
      changes: [],
      summary: EMPTY_SUMMARY,
      movedIds: new Set(),
      planSquads,
      colorOverrides: overrides,
      filtered: empty,
    };
  }

  const model = buildOrgModel(snapshot, scenario);
  const changes = scenario ? diffScenario(model, scenario, squads) : [];
  const movedIds = movedPersonIds(changes);
  return {
    model,
    changes,
    summary: summarizeChanges(changes),
    movedIds,
    planSquads,
    colorOverrides: overrides,
    // Recomputed with the model, because who survives a filter depends on both
    // the filter and the org it is applied to.
    filtered: visibleAfterFilters({ model, changes, movedIds, squads: planSquads, ...filters }),
  };
}

/** The people a set of changes is about — team changes name at most one. */
function subjectsOf(changes: Change[]): string[] {
  return changes.map(changePersonId).filter((id): id is string => id !== null);
}

/**
 * Take the directory the server just returned and re-derive with it. A base
 * team moving changes what the open scenario is proposing *against*, so the
 * change list has to be recomputed, not just the board.
 */
function adoptSquads(
  set: (partial: Partial<AppState>) => void,
  get: () => AppState,
  payload: { squads: Squad[]; updatedAt: string; shared?: boolean },
): void {
  const squads: SquadDirectory = { updatedAt: payload.updatedAt, squads: payload.squads };
  set({
    squads,
    ...(payload.shared === undefined
      ? {}
      : {
          sharedTeams: payload.shared,
          // Nothing here can edit a published base, so leaving the board aimed
          // at it would mean every edit erroring.
          ...(payload.shared ? { teamEditTarget: 'scenario' as const } : {}),
        }),
    ...derive(get().snapshot, get().scenario, squads, get()),
  });
}

/**
 * The way a scenario was left: what was focused, folded, filtered and coloured.
 *
 * Kept per scenario rather than per app, because it belongs to the plan — you
 * come back to "the Q4 reorg, on the software org, coloured by team" and expect
 * to find it that way. Stored in this browser rather than in the scenario file:
 * it is a view, not a proposal, and two tabs on one scenario would otherwise
 * fight over it.
 */
export interface ScenarioView {
  focusId: string | null;
  collapsedIds: string[];
  colorBy: ColorBy;
  groupLevels: GroupBy[];
  view: ChartView;
  departmentFilter: string[];
  squadFilter: string[];
  onlyChanged: boolean;
}

export function viewOf(state: AppState): ScenarioView {
  return {
    focusId: state.focusId,
    collapsedIds: state.collapsedIds,
    colorBy: state.colorBy,
    groupLevels: state.groupLevels,
    view: state.view,
    departmentFilter: state.departmentFilter,
    squadFilter: state.squadFilter,
    onlyChanged: state.onlyChanged,
  };
}

/** A remembered view minus the keys the caller wants to decide for itself. */
function viewWithout(view: ScenarioView, ...keys: (keyof ScenarioView)[]): Partial<ScenarioView> {
  const out: Partial<ScenarioView> = { ...view };
  for (const key of keys) delete out[key];
  return out;
}

export function sameView(a: ScenarioView, b: ScenarioView): boolean {
  return (
    a.focusId === b.focusId &&
    a.colorBy === b.colorBy &&
    a.view === b.view &&
    a.onlyChanged === b.onlyChanged &&
    sameList(a.collapsedIds, b.collapsedIds) &&
    sameList(a.groupLevels, b.groupLevels) &&
    sameList(a.departmentFilter, b.departmentFilter) &&
    sameList(a.squadFilter, b.squadFilter)
  );
}

/**
 * How big a picture of the team board has to be.
 *
 * Laid out from the same tree the board draws, rather than measured off the
 * DOM: the canvas is transformed by whatever zoom somebody left it at, so its
 * element is not the size of its contents. It is the same call the board makes,
 * which is why `buildGroupTree` is a shared function and not a memo inside it.
 */
function teamBoardBounds(state: AppState): { minX: number; maxX: number; minY: number; maxY: number } {
  const { model, groupLevels, focusId, teamEditTarget, foldedGroups } = state;
  if (!model) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };

  const squads = teamEditTarget === 'base' ? state.squads : state.planSquads;
  const tree = buildGroupTree({ model, levels: groupLevels, squads, focusId });
  const nodes = new Map(
    [...tree.nodes].map(([id, node]) => [
      id,
      { id, childIds: node.childIds, memberCount: node.members.length },
    ]),
  );
  const folded = resolveFolded(tree, foldedGroups[groupingKey(groupLevels, focusId)]);
  return layoutGroups(nodes, tree.rootIds, folded).bounds;
}

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** The slice kept in local storage between sessions. */
type PersistedState = Pick<
  AppState,
  | 'theme'
  | 'panelOpen'
  | 'panelTab'
  | 'collapsedIds'
  | 'foldedGroups'
  | 'foldedChangeKinds'
  | 'tableViews'
  | 'focusId'
  | 'showMinimap'
  | 'view'
  | 'groupLevels'
  | 'teamEditTarget'
  | 'colorBy'
  | 'viewByScenario'
>;

let toastSeq = 0;

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => {
      /**
       * Undo and redo, which are the same act aimed in two directions.
       *
       * The server answers with the whole scenario and the fresh history, so one
       * round trip is enough and the panel cannot end up disagreeing with the
       * chart about what just happened.
       */
      const step = async (direction: 'undo' | 'redo'): Promise<void> => {
        const { scenario, snapshot } = get();
        if (!scenario || !snapshot) return;
        try {
          const payload = await (direction === 'undo' ? api.undo : api.redo)(scenario.id);
          const derived = derive(snapshot, payload.scenario, get().squads, get());
          set({
            scenario: payload.scenario,
            ...derived,
            ...(payload.history ? { history: payload.history } : {}),
          });
          // Whatever moved has to end up on screen, same as any other change.
          get().ensureVisible(subjectsOf(derived.changes));
          get().toast(
            'info',
            payload.messages?.[0] ?? `${direction === 'undo' ? 'Undid' : 'Redid'} a change.`,
          );
          void get().reloadScenarioList();
        } catch (error) {
          get().toast('error', error instanceof Error ? error.message : String(error));
        }
      };

      return {
        model: null,
        changes: [],
        summary: EMPTY_SUMMARY,
        movedIds: new Set<string>(),
        planSquads: emptySquadDirectory(),
        colorOverrides: new Map<string, string>(),
        filtered: { keep: null, matched: 0, context: 0, total: 0 },

        snapshot: null,
        meta: null,
        stats: null,
        snowflakeConfigured: false,
        scenario: null,
        scenarioList: [],
        squads: emptySquadDirectory(),
        sharedTeams: false,
        status: 'loading',
        error: null,
        refreshing: false,
        live: false,
        viewers: [],
        role: 'owner',
        pickerApiKey: null,
        pickerAppId: null,
        buildId: null,
        updateReady: false,
        copyPrompt: false,
        exporting: false,

        // Follows the browser until somebody says otherwise; see lib/theme.
        theme: 'system',
        viewByScenario: {},
        panelOpen: false,
        panelTab: 'changes',
        collapsedIds: [],
        foldedGroups: {},
        fitSignal: 0,
        boardDrag: null,
        foldedChangeKinds: [],
        tableViews: {},
        sheetsAvailable: false,
        changesTableOpen: false,
        focusId: null,
        showMinimap: true,
        view: 'reporting',
        // Defaults to the base, which is where a first-time setup belongs. The
        // board says loudly which one is being edited.
        teamEditTarget: 'base',
        // Location is the one dimension almost everybody wants first: it is
        // stable, it is on every card already, and it answers 'who is where'.
        colorBy: 'location',
        // Tive's own shape: everyone under a top-level leader, split by
        // department, then the squads that actually deliver the work.
        groupLevels: ['org', 'department', 'squad'],

        query: '',
        departmentFilter: [],
        squadFilter: [],
        onlyChanged: false,
        selectedId: null,
        hireUnderId: null,
        paletteOpen: false,
        draggingId: null,
        drop: { targetId: null, ok: false, reason: null },
        pulseId: null,
        toasts: [],
        history: null,

        // -----------------------------------------------------------------------

        bootstrap: async () => {
          set({ status: 'loading', error: null });
          try {
            // A tab opens whichever scenario its URL names, so two tabs can sit
            // on two scenarios. Only a tab that has not chosen yet falls back to
            // the server's default.
            const pinned = pinnedScenarioId();
            const [org, list, health] = await Promise.all([
              api.org(),
              api.listScenarios(),
              // Whether this server can offer the Drive picker. Failing to ask is
              // not worth refusing to start over: no key just means no button.
              api.health().catch(() => null),
              get().loadSquads(),
            ]);

            let active: Awaited<ReturnType<typeof api.activeScenario>> | null = null;
            let missingPin: string | null = null;

            /*
             * This tab may exist because somebody chose "Open with" on a scenario
             * in Drive. That wins over the pinned and the active scenario — it is
             * the most explicit thing anyone has said about what to show.
             *
             * A failure is a toast and nothing more: the app still has an org to
             * draw, and refusing to start over a file we could not read would be
             * a worse answer than starting without it.
             */
            const driveFileId = driveOpenFileId();
            if (driveFileId) {
              clearDriveState();
              try {
                active = await api.openDriveFile(driveFileId);
              } catch (error) {
                get().toast('error', error instanceof Error ? error.message : String(error));
              }
            }
            if (!active && pinned) {
              try {
                active = await api.getScenario(pinned);
              } catch {
                missingPin = pinned;
              }
            }
            active ??= await api.activeScenario();
            /*
             * There may be no scenario at all — a first run invents nothing now,
             * so the app shows the chart and asks for one. Everything below is
             * written against `plan` being null, and `FirstRun` is what fills it.
             */
            const plan = active.scenario;
            if (plan) pinScenario(plan.id);

            const derived = derive(org.snapshot, plan, get().squads, get());
            const remembered = plan ? get().viewByScenario[plan.id] : undefined;
            /*
             * What is folded, or the default when this browser has nothing to say.
             *
             * The top of the org and its direct reports, and nothing below that.
             * Expanded, a 330-person company is 90,000px wide and has to be zoomed
             * out past the point where a card can be read — but a single card is a
             * cold start, and the first row is the shape of the company.
             */
            const theTopOnly = () => (derived.model ? [...defaultCollapsed(derived.model, 1)] : []);

            const previous = (remembered?.collapsedIds ?? get().collapsedIds).filter(
              (id) => derived.model?.byId.has(id) ?? false,
            );

            /*
             * Nothing recognised means nothing remembered — a plan from another
             * org, or ids that have since left. Falling through to an empty list
             * there is what made a first look at a refreshed org arrive fully
             * expanded, which is the one state this is trying to avoid.
             */
            const collapsedIds = previous.length > 0 ? previous : theTopOnly();

            // The scenario is opened the way it was left — focus, folding,
            // filters, colouring — or with the app's defaults the first time.
            const focusId = remembered?.focusId ?? get().focusId;
            set({
              snapshot: org.snapshot,
              meta: org.meta,
              stats: org.stats,
              snowflakeConfigured: org.snowflakeConfigured,
              scenario: plan,
              scenarioList: list.scenarios,
              role: roleIn(list.scenarios, plan?.id),
              pickerApiKey: health?.pickerApiKey ?? null,
              pickerAppId: health?.pickerAppId ?? null,
              sheetsAvailable: health?.sheets ?? false,
              ...(remembered ? viewWithout(remembered, 'collapsedIds', 'focusId') : {}),
              focusId: focusId && derived.model?.byId.has(focusId) ? focusId : null,
              collapsedIds,
              status: 'ready',
              ...derived,
            });
            get().noteBuild(health?.buildId);
            set({ filtered: visibleAfterFilters(get()) });

            // Anything this scenario changes should be on screen when it opens.
            get().ensureVisible(subjectsOf(derived.changes));

            if (missingPin && plan) {
              get().toast('error', `That scenario is gone. Opened “${plan.name}” instead.`);
            } else if (missingPin) {
              get().toast('error', 'That scenario is gone. Start a new one below.');
            }
            for (const warning of derived.model?.warnings ?? []) get().toast('error', warning);
          } catch (error) {
            set({ status: 'error', error: error instanceof Error ? error.message : String(error) });
          }
        },

        refreshOrg: async () => {
          set({ refreshing: true });
          try {
            const { snapshot, meta } = await api.refreshOrg();
            set({ snapshot, meta, ...derive(snapshot, get().scenario, get().squads, get()) });
            get().toast('success', `Pulled ${snapshot.people.length} people from Snowflake.`);
          } catch (error) {
            get().toast('error', error instanceof ApiError ? error.message : String(error));
          } finally {
            set({ refreshing: false });
          }
        },

        applyOps: async (ops) => {
          const { snapshot, scenario, squads } = get();
          if (!snapshot || !scenario) return false;

          // Optimistic: run the same core logic the server will run.
          let optimistic = scenario;
          const messages: string[] = [];
          try {
            for (const op of ops) {
              const result = applyOperation(snapshot, optimistic, op, { squads });
              optimistic = result.scenario;
              messages.push(result.message);
            }
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
            return false;
          }

          set({ scenario: optimistic, ...derive(snapshot, optimistic, squads, get()) });

          // Keep anyone we just touched on screen, even if their new manager was collapsed.
          const touched = ops.flatMap((op) =>
            'personId' in op ? [op.personId] : op.op === 'addHire' ? [op.hire.managerId] : [],
          );
          get().ensureVisible(touched);

          try {
            const payload = await api.applyOps(scenario.id, ops);
            /*
             * Only if it is not stale. Another save may have started and finished
             * while this one was in flight, and taking an older answer would undo
             * it on screen while leaving it on the server.
             */
            if (isCurrent(get().scenario, payload.scenario)) {
              set({
                scenario: payload.scenario,
                ...derive(snapshot, payload.scenario, get().squads, get()),
              });
            }
            get().ensureVisible(touched);
            void get().reloadScenarioList();
            return true;
          } catch (error) {
            /*
             * Roll back to what the server still believes — unless something
             * newer has landed since, in which case rolling back to the state
             * this call started from would throw that away too.
             */
            if (isCurrent(get().scenario, scenario)) {
              set({ scenario, ...derive(snapshot, scenario, squads, get()) });
            }
            /*
             * A plan shared for viewing is not an error to report, it is a
             * question to ask: the change is fine, it just has nowhere of theirs
             * to land. The prompt only appears once somebody tries to edit —
             * reading somebody`s plan should cost them nothing and leave no file
             * in your Drive you never asked for.
             */
            if (error instanceof ApiError && error.code === 'read-only') {
              set({ role: 'reader', copyPrompt: true });
              return false;
            }
            get().toast('error', error instanceof Error ? error.message : String(error));
            return false;
          }
        },

        loadHistory: async () => {
          const { scenario } = get();
          if (!scenario) return;
          try {
            set({ history: await api.history(scenario.id) });
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        /*
         * Undo and redo are not optimistic, unlike every other mutation here.
         * What they undo is whatever the *server's* log says came last, which
         * after Claude has edited the same scenario is not necessarily what this
         * tab did — so guessing at the result and rolling back would show the
         * wrong change disappearing for a moment.
         */
        undo: () => step('undo'),
        redo: () => step('redo'),

        selectScenario: async (id, opts) => {
          try {
            // This also moves the server's default, which is only what a *new*
            // tab opens with; tabs already open are unaffected.
            const payload = await api.setActiveScenario(id);
            const { snapshot } = get();
            const plan = payload.scenario;
            // Passing null closes the plan rather than opening one, which is
            // how you get back to the chart with nothing layered on it.
            if (!plan) {
              set({ scenario: null, ...derive(snapshot, null, get().squads, get()) });
              void get().reloadScenarioList();
              return;
            }
            const remembered = get().viewByScenario[plan.id];
            const derived = derive(snapshot, plan, get().squads, get());
            pinScenario(plan.id);
            set({ scenario: plan, ...(remembered ?? {}), ...derived });
            if (remembered) set({ filtered: visibleAfterFilters(get()) });
            get().ensureVisible(subjectsOf(derived.changes));
            void get().reloadScenarioList();
            const kind = opts?.announceAs ?? 'info';
            get().toast(kind, kind === 'remote' ? `Opened “${plan.name}”.` : `Switched to “${plan.name}”.`);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        newScenario: async (name, description) => {
          try {
            const payload = await api.createScenario(name, description);
            const { snapshot } = get();
            pinScenario(payload.scenario.id);
            set({ scenario: payload.scenario, ...derive(snapshot, payload.scenario, get().squads, get()) });
            void get().reloadScenarioList();
            get().toast('success', `Created “${payload.scenario.name}”.`);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        renameScenario: async (name, description) => {
          const { scenario, snapshot } = get();
          if (!scenario) return;
          try {
            const payload = await api.renameScenario(scenario.id, {
              name,
              ...(description === undefined ? {} : { description }),
            });
            set({ scenario: payload.scenario, ...derive(snapshot, payload.scenario, get().squads, get()) });
            void get().reloadScenarioList();
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        duplicateScenario: async () => {
          const { scenario } = get();
          if (!scenario) return;
          try {
            const payload = await api.duplicateScenario(scenario.id);
            await get().selectScenario(payload.scenario.id);
            get().toast('success', `Duplicated as “${payload.scenario.name}”.`);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        deleteScenario: async (id) => {
          try {
            const { activeId } = await api.deleteScenario(id);
            await get().reloadScenarioList();
            await get().selectScenario(activeId);
            get().toast('info', 'Scenario deleted.');
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        /**
         * Send a grid to the server to become a spreadsheet in Drive.
         *
         * The link is a toast rather than a redirect: somebody exporting a table
         * is usually mid-thought, and a new tab stealing focus loses whatever they
         * were about to do next.
         */
        exportSheet: async (title, grid) => {
          try {
            const sheet = await api.exportSheet(title, grid);
            get().toast('success', 'Sheet created in your Drive.', sheet.url);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        exportScenario: async () => {
          const { scenario } = get();
          if (!scenario) return;
          try {
            const file = await api.exportScenario(scenario.id);
            downloadJson(`${slugify(scenario.name) || 'scenario'}.json`, file);
            get().toast('success', 'Scenario exported.');
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        /**
         * A 1920x1080 PNG of whatever view is open, for a slide.
         *
         * Fixed size on purpose: an export that follows the window gives a
         * different picture every time and none of them fit a deck. The chart is
         * captured from React Flow's viewport with the layout's own bounds, since
         * the viewport element has no size of its own.
         */
        exportPng: async () => {
          const { model, view, scenario, collapsedIds, focusId } = get();
          if (!model) return;

          // Above 180 cards React Flow only renders what is on screen, which
          // would export a chart with holes in it.
          set({ exporting: true });
          try {
            await new Promise((resolve) => setTimeout(resolve, 90));

            const styles = getComputedStyle(document.documentElement);
            const background = styles.getPropertyValue('--color-canvas').trim() || '#ffffff';

            /*
             * One selector for both views now that the board is a canvas too.
             * Only one of them is ever mounted, so this cannot be ambiguous.
             */
            const element = document.querySelector<HTMLElement>('.react-flow__viewport');
            if (!element) throw new Error('Nothing to export yet.');

            const bounds =
              view === 'teams'
                ? teamBoardBounds(get())
                : layoutOrg(model, { rootId: focusId, collapsed: new Set(collapsedIds) }).bounds;

            const dataUrl = await capturePng(element, bounds, { background });
            const name = `${slugify(scenario?.name ?? 'org') || 'org'}-${view === 'teams' ? 'teams' : 'chart'}.png`;
            downloadDataUrl(name, dataUrl);
            get().toast('success', `Saved ${name} at 1920×1080.`);
          } catch (error) {
            get().toast(
              'error',
              `Could not save the image: ${error instanceof Error ? error.message : String(error)}`,
            );
          } finally {
            set({ exporting: false });
          }
        },

        importScenario: async (file) => {
          try {
            const payload = await api.importScenario(file);
            const { snapshot } = get();
            pinScenario(payload.scenario.id);
            set({ scenario: payload.scenario, ...derive(snapshot, payload.scenario, get().squads, get()) });
            void get().reloadScenarioList();
            get().toast(
              'success',
              `Imported “${payload.scenario.name}” with ${payload.summary.total} change(s).`,
            );
            for (const warning of payload.importWarnings ?? []) get().toast('error', warning);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        openFromDrive: async () => {
          const apiKey = get().pickerApiKey;
          const appId = get().pickerAppId;
          const driveToken = currentSession()?.driveToken;
          /*
           * Both, not either. Without the app id the picker still opens and
           * still closes as though it worked, and the file it handed back is
           * then refused — so a missing project number has to read as "not set
           * up" here rather than as a broken share later.
           */
          if (!apiKey || !appId || !driveToken) {
            get().toast('error', 'Opening from Drive is not set up on this server.');
            return;
          }
          try {
            const picked = await pickScenarioFile({ apiKey, appId, driveToken });
            // Closed without choosing. Nothing happened, and reporting the
            // absence of an action as an event is just noise.
            if (!picked) return;

            const payload = await api.openDriveFile(picked.id);
            pinScenario(payload.scenario.id);
            set({
              scenario: payload.scenario,
              ...derive(get().snapshot, payload.scenario, get().squads, get()),
            });
            await get().reloadScenarioList();
            get().ensureVisible(subjectsOf(get().changes));
            get().toast('success', `Opened “${payload.scenario.name}”.`);
            for (const warning of payload.importWarnings ?? []) get().toast('error', warning);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        reloadScenarioList: async () => {
          try {
            const list = await api.listScenarios();
            set({ scenarioList: list.scenarios, role: roleIn(list.scenarios, get().scenario?.id) });
          } catch {
            /* non-critical */
          }
        },

        /*
         * The five team actions below each go one of two ways, depending on
         * `teamEditTarget`: to /api/squads, which changes how the teams *are*, or
         * through applyOps, which proposes a change inside the open scenario.
         * Routing here rather than in the components means one place decides, and
         * one place is testable.
         *
         * The base path re-reads the whole directory from the server's answer
         * rather than patching locally: the list is tiny and the server is the one
         * that reconciles it against the org.
         */
        loadSquads: async () => {
          try {
            const payload = await api.listSquads();
            adoptSquads(set, get, payload);
            for (const warning of payload.warnings) get().toast('error', warning);
          } catch {
            /* Squads are an overlay: the chart is still usable without them. */
          }
        },

        createSquad: async (name, description, parentId) => {
          if (get().teamEditTarget === 'scenario') {
            await get().applyOps([
              {
                op: 'addSquad',
                name,
                ...(description ? { description } : {}),
                ...(parentId ? { parentId } : {}),
              },
            ]);
            return;
          }
          try {
            const payload = await api.createSquad(name, description, parentId ?? null);
            adoptSquads(set, get, payload);
            get().toast('success', `Created “${payload.squad.name}”.`);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        renameSquad: async (id, name) => {
          if (get().teamEditTarget === 'scenario') {
            await get().applyOps([{ op: 'renameSquad', squadId: id, name }]);
            return;
          }
          try {
            const payload = await api.updateSquad(id, { name });
            adoptSquads(set, get, payload);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        /*
         * Colour is a property of the team itself rather than of a plan — it is
         * usually pinned to match something outside this app — so it is only ever
         * written to the shared directory.
         */
        setSquadColor: async (id, color) => {
          try {
            adoptSquads(set, get, await api.updateSquad(id, { color }));
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        setSquadParent: async (id, parentId) => {
          if (get().teamEditTarget === 'scenario') {
            await get().applyOps([{ op: 'setSquadParent', squadId: id, parentId }]);
            return;
          }
          try {
            adoptSquads(set, get, await api.updateSquad(id, { parentId }));
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        deleteSquad: async (id) => {
          if (get().teamEditTarget === 'scenario') {
            await get().applyOps([{ op: 'disbandSquad', squadId: id }]);
            return;
          }
          const name = get().squads.squads.find((squad) => squad.id === id)?.name;
          try {
            const payload = await api.deleteSquad(id);
            adoptSquads(set, get, payload);
            get().toast('info', name ? `Deleted “${name}”.` : 'Team deleted.');
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        setSquadMember: async (id, personId, role) => {
          if (get().teamEditTarget === 'scenario') {
            await get().applyOps([{ op: 'setSquadMember', squadId: id, personId, role }]);
            return;
          }
          try {
            const payload = await api.setSquadMember(id, personId, role);
            adoptSquads(set, get, payload);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        removeSquadMember: async (id, personId) => {
          if (get().teamEditTarget === 'scenario') {
            await get().applyOps([{ op: 'removeSquadMember', squadId: id, personId }]);
            return;
          }
          try {
            const payload = await api.removeSquadMember(id, personId);
            adoptSquads(set, get, payload);
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        /*
         * A move, which the board's drag calls for and the details pane
         * deliberately does not: somebody who should be on two teams is added
         * to the second one there, and dragging them says the opposite.
         *
         * Added to the new team before being taken off the old one. In a plan
         * both ops go in one batch, which the ops endpoint writes atomically;
         * against the base they are two calls and cannot be, so the order is
         * the guardrail. Ending up on both teams is visible and correctable;
         * ending up on neither is somebody quietly lost off the board.
         */
        moveSquadMember: async (fromId, toId, personId, role) => {
          if (get().teamEditTarget === 'scenario') {
            await get().applyOps([
              { op: 'setSquadMember', squadId: toId, personId, role },
              { op: 'removeSquadMember', squadId: fromId, personId },
            ]);
            return;
          }
          try {
            adoptSquads(set, get, await api.setSquadMember(toId, personId, role));
            adoptSquads(set, get, await api.removeSquadMember(fromId, personId));
          } catch (error) {
            get().toast('error', error instanceof Error ? error.message : String(error));
          }
        },

        setBoardDrag: (drag) => set({ boardDrag: drag }),

        adoptRemoteScenario: (scenario, message) => {
          const { snapshot, scenario: current } = get();
          // Tabs are independent: a change to a scenario this tab does not have
          // open is somebody else's business, and must never move this one.
          if (!current || scenario.id !== current.id) return;

          const previousChangeIds = new Set(get().changes.map((change) => change.id));
          const derived = derive(snapshot, scenario, get().squads, get());
          set({ scenario, ...derived });

          // Reveal whatever Claude just changed, then pan to it — otherwise the
          // expansion can push the new cards off the edge of the viewport.
          get().ensureVisible(subjectsOf(derived.changes));
          const firstNew = derived.changes.find((change) => !previousChangeIds.has(change.id));
          if (firstNew) get().pulse(changePersonId(firstNew));

          if (message) get().toast('remote', message);
        },

        setLive: (live) => set({ live }),

        setViewers: (viewers) => set({ viewers }),

        /*
         * The first build seen is the one this page is running; anything different
         * afterwards means a deploy has landed underneath it. Once flagged it
         * stays flagged: the notice should not vanish because a later frame
         * happened to arrive from the same server.
         */
        noteBuild: (buildId) => {
          if (!buildId) return;
          const known = get().buildId;
          if (known === null) {
            set({ buildId });
            return;
          }
          if (known !== buildId) set({ updateReady: true });
        },

        /*
         * The answer to a refused edit, not a thing to do in advance. Somebody
         * who only wants to read a plan should cost its owner nothing and end
         * up with no file in their Drive they did not ask for.
         */
        copyForEditing: async () => {
          const { scenario } = get();
          if (!scenario) return;
          set({ copyPrompt: false });
          await get().duplicateScenario();
        },

        dismissCopyPrompt: () => set({ copyPrompt: false }),

        setView: (view) => set({ view }),
        setTeamEditTarget: (teamEditTarget) => set({ teamEditTarget }),
        setColorBy: (colorBy) => set({ colorBy }),
        /**
         * Empty levels are dropped, so a "None" in the middle collapses cleanly,
         * and a level can only appear once — grouping by team inside team nests
         * every box inside a copy of itself.
         */
        setGroupLevels: (levels) => set({ groupLevels: [...new Set(levels)].slice(0, 3) }),

        // -----------------------------------------------------------------------

        toggleCollapse: (id) => {
          const collapsed = new Set(get().collapsedIds);
          if (collapsed.has(id)) collapsed.delete(id);
          else collapsed.add(id);
          set({ collapsedIds: [...collapsed] });
        },

        setCollapsed: (ids) => set({ collapsedIds: [...ids] }),

        /*
         * Toggled against the set that is actually on screen, which is not the
         * same as the stored one: with nothing stored the board is showing the
         * default, so writing a one-id list on the first click would say every
         * *other* card had just been unfolded — which is exactly what it did.
         * The current answer is resolved first and the whole of it written back.
         */
        toggleGroupFold: (id) => {
          const state = get();
          if (!state.model) return;
          const key = groupingKey(state.groupLevels, state.focusId);
          const tree = buildGroupTree({
            model: state.model,
            levels: state.groupLevels,
            squads: state.teamEditTarget === 'base' ? state.squads : state.planSquads,
            focusId: state.focusId,
          });

          const folded = resolveFolded(tree, state.foldedGroups[key]);
          if (folded.has(id)) folded.delete(id);
          else folded.add(id);
          set({ foldedGroups: { ...state.foldedGroups, [key]: [...folded] } });
        },

        setTableView: (table, view) => set({ tableViews: { ...get().tableViews, [table]: view } }),

        setChangesTableOpen: (open) => set({ changesTableOpen: open }),

        toggleChangeGroup: (kind) => {
          const folded = new Set(get().foldedChangeKinds);
          if (folded.has(kind)) folded.delete(kind);
          else folded.add(kind);
          set({ foldedChangeKinds: [...folded] });
        },

        /**
         * Expand whatever is needed for these people to be on screen. Without
         * this, dropping someone onto a collapsed manager would make them
         * disappear, and changes Claude makes could land out of sight.
         */
        ensureVisible: (ids) => {
          const { model } = get();
          if (!model) return;
          const collapsed = new Set(get().collapsedIds);
          let changed = false;
          for (const id of ids) {
            let current = model.byId.get(id);
            const seen = new Set<string>();
            while (current?.managerId && !seen.has(current.managerId)) {
              seen.add(current.managerId);
              if (collapsed.delete(current.managerId)) changed = true;
              current = model.byId.get(current.managerId);
            }
          }
          if (changed) set({ collapsedIds: [...collapsed] });
        },

        expandAll: () => set({ collapsedIds: [] }),

        collapseToDepth: (depth) => {
          const { model } = get();
          if (!model) return;
          // Framed as well, because what was filling the screen has just gone.
          set({ collapsedIds: [...defaultCollapsed(model, depth)], fitSignal: get().fitSignal + 1 });
        },

        /** Every card on the team board, open. */
        expandAllGroups: () => {
          const state = get();
          if (!state.model) return;
          set({
            foldedGroups: { ...state.foldedGroups, [groupingKey(state.groupLevels, state.focusId)]: [] },
          });
        },

        collapseAllGroups: () => {
          const state = get();
          if (!state.model) return;
          const tree = buildGroupTree({
            model: state.model,
            levels: state.groupLevels,
            squads: state.teamEditTarget === 'base' ? state.squads : state.planSquads,
            focusId: state.focusId,
          });
          set({
            foldedGroups: {
              ...state.foldedGroups,
              [groupingKey(state.groupLevels, state.focusId)]: [...foldableIds(tree.nodes)],
            },
            fitSignal: state.fitSignal + 1,
          });
        },

        fitLater: () => set({ fitSignal: get().fitSignal + 1 }),

        /** Expand every ancestor so a person is on screen. */
        revealPerson: (id) => {
          const { model } = get();
          if (!model) return;
          const collapsed = new Set(get().collapsedIds);
          let current = model.byId.get(id);
          while (current?.managerId) {
            collapsed.delete(current.managerId);
            current = model.byId.get(current.managerId);
          }
          set({ collapsedIds: [...collapsed], selectedId: id });
          get().pulse(id);
        },

        setFocus: (id) => set({ focusId: id, selectedId: id ?? get().selectedId }),
        setTheme: (theme) => set({ theme }),
        togglePanel: () => set({ panelOpen: !get().panelOpen }),
        openPanel: (panelTab) => set({ panelOpen: true, panelTab }),
        setPanelTab: (panelTab) => set({ panelTab }),
        toggleMinimap: () => set({ showMinimap: !get().showMinimap }),
        setQuery: (query) => set({ query }),
        /*
         * Each of these recomputes who is on screen, because the chart draws only
         * what the filters left standing rather than fading the rest out.
         */
        /*
         * Each of these recomputes who is on screen, because the chart draws only
         * what the filters left standing rather than fading the rest out.
         */
        setDepartmentFilter: (departmentFilter) => {
          set({ departmentFilter, filtered: visibleAfterFilters({ ...get(), departmentFilter }) });
        },
        setSquadFilter: (squadFilter) => {
          set({ squadFilter, filtered: visibleAfterFilters({ ...get(), squadFilter }) });
        },
        setOnlyChanged: (onlyChanged) => {
          set({ onlyChanged, filtered: visibleAfterFilters({ ...get(), onlyChanged }) });
        },
        select: (selectedId) => set({ selectedId }),
        setHireUnder: (hireUnderId) => set({ hireUnderId }),
        setPalette: (paletteOpen) => set({ paletteOpen }),
        startDrag: (draggingId) => set({ draggingId }),
        setDrop: (drop) => set({ drop }),
        /** Pan the chart to this person and flash their card. */
        pulse: (pulseId) => {
          set({ pulseId });
          if (pulseId === null) return;
          setTimeout(() => {
            if (get().pulseId === pulseId) set({ pulseId: null });
          }, 2200);
        },

        toast: (kind, message, href) => {
          toastSeq += 1;
          const id = `t${toastSeq}`;
          set({ toasts: [...get().toasts, { id, kind, message, ...(href ? { href } : {}) }] });
          // Longer when there is something to click: a toast that vanishes before
          // anybody reaches the link may as well not have carried one.
          setTimeout(() => get().dismissToast(id), kind === 'error' || href ? 9000 : 4500);
        },

        dismissToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
      };
    },
    {
      name: 'tive-org-planning',
      version: 2,
      /**
       * v1 stored a concrete theme for everyone, because the default was dark
       * rather than "follow the browser". There is no way to tell a stored
       * default from a stored choice, so everyone goes back to following the
       * browser once; anyone who wants dark can say so again.
       */
      migrate: (persisted, from) => {
        const stored = (persisted ?? {}) as PersistedState;
        return from >= 2 ? stored : { ...stored, theme: 'system' };
      },
      partialize: (state): PersistedState => ({
        theme: state.theme,
        panelOpen: state.panelOpen,
        panelTab: state.panelTab,
        collapsedIds: state.collapsedIds,
        foldedGroups: state.foldedGroups,
        foldedChangeKinds: state.foldedChangeKinds,
        tableViews: state.tableViews,
        focusId: state.focusId,
        showMinimap: state.showMinimap,
        view: state.view,
        groupLevels: state.groupLevels,
        teamEditTarget: state.teamEditTarget,
        colorBy: state.colorBy,
        viewByScenario: state.viewByScenario,
      }),
    },
  ),
);

/*
 * Remembering the view is done here rather than in each of the dozen setters
 * that can change it: focus, folding, filters, colouring and which view is
 * open all belong to the scenario, and threading a save call through every one
 * of them is how one gets forgotten.
 *
 * Writing back re-notifies, which is harmless: the second pass finds the stored
 * view already equal and stops.
 */
useAppStore.subscribe((state) => {
  const id = state.scenario?.id;
  if (!id || state.status !== 'ready') return;

  const current = viewOf(state);
  const stored = state.viewByScenario[id];
  if (stored && sameView(stored, current)) return;

  useAppStore.setState({ viewByScenario: { ...state.viewByScenario, [id]: current } });
});
